import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Plus,
  Calendar,
  Users,
  Clock,
  Tag,
  X,
  Loader2
} from 'lucide-react';
import api, { Project, Client } from '../services/api';

const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    client: '',
    project_type: 'Marketing' as 'AI' | 'Marketing',
    status: 'active' as 'active' | 'paused' | 'completed' | 'cancelled',
    description: '',
    progress: 0,
    budget: '',
    deadline: ''
  });

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const [projectsResponse, clientsResponse] = await Promise.all([
        api.getProjects({
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
          project_type: selectedType !== 'all' ? selectedType : undefined,
          search: searchQuery || undefined
        }),
        api.getClients()
      ]);
      setProjects(projectsResponse.results);
      setClients(clientsResponse.results);
      setError(null);
    } catch (err) {
      setError('Failed to load projects');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedStatus, selectedType, searchQuery]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const projectData = {
        ...formData,
        budget: formData.budget || null,
        deadline: formData.deadline || null
      };

      if (editingProject) {
        await api.updateProject(editingProject.id, projectData);
      } else {
        await api.createProject(projectData);
      }
      setShowModal(false);
      setEditingProject(null);
      resetForm();
      fetchProjects();
    } catch (err) {
      console.error(err);
      alert('Failed to save project');
    }
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      client: project.client,
      project_type: project.project_type,
      status: project.status,
      description: project.description,
      progress: project.progress,
      budget: project.budget || '',
      deadline: project.deadline || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Delete this project?')) {
      try {
        await api.deleteProject(id);
        fetchProjects();
      } catch (err) {
        console.error(err);
        alert('Failed to delete project');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      client: '',
      project_type: 'Marketing',
      status: 'active',
      description: '',
      progress: 0,
      budget: '',
      deadline: ''
    });
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'active': return 'status-badge status-active';
      case 'paused': return 'status-badge status-warning';
      case 'completed': return 'status-badge status-completed';
      default: return 'status-badge status-error';
    }
  };

  if (loading && projects.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black">
      {/* Header - Compact */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-base font-semibold text-gray-900 dark:text-white">Projects</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">AI and Marketing projects</p>
            </div>
            <button
              onClick={() => { resetForm(); setEditingProject(null); setShowModal(true); }}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4" />
              New Project
            </button>
          </div>
        </div>
      </div>

      {/* Filters - Compact */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search projects..."
                className="input pl-7"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <select className="select" value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
              <option value="all">All Types</option>
              <option value="AI">AI Projects</option>
              <option value="Marketing">Marketing</option>
            </select>
            <select className="select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="bg-status-error/10 border border-status-error/30 rounded p-2 text-xs text-status-error">{error}</div>
        </div>
      )}

      {/* Projects Grid - Compact */}
      <div className="max-w-7xl mx-auto px-3 py-3">
        {projects.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-xs text-gray-500 dark:text-gray-400">No projects found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {projects.map((project) => (
              <div key={project.id} className="panel">
                <div className="p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{project.name}</h3>
                        <span className={`status-badge ${project.project_type === 'AI' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'}`}>
                          {project.project_type}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{project.client_name}</p>
                    </div>
                    <span className={getStatusClass(project.status)}>{project.status}</span>
                  </div>

                  {project.description && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">{project.description}</p>
                  )}

                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 mb-2 text-xs">
                    {project.deadline && (
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        <span>{new Date(project.deadline).toLocaleDateString()}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <Users className="w-3 h-3 text-gray-400" />
                      <span>{project.team_count} members</span>
                    </div>
                    {project.budget && (
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Tag className="w-3 h-3 text-gray-400" />
                        <span>${parseFloat(project.budget).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span>{project.progress}% done</span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-2">
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                      <div className="h-full bg-primary-500 rounded-full transition-all" style={{ width: `${project.progress}%` }} />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-1">
                    <button onClick={() => handleEdit(project)} className="btn btn-ghost text-blue-600">Edit</button>
                    <button onClick={() => handleDelete(project.id)} className="btn btn-ghost text-red-600">Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal - Compact */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
          <div className="bg-white dark:bg-gray-900 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto border border-light-border dark:border-dark-border">
            <div className="flex items-center justify-between p-3 border-b border-light-border dark:border-dark-border">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                {editingProject ? 'Edit Project' : 'New Project'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-3 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Client</label>
                <select required value={formData.client} onChange={(e) => setFormData({ ...formData, client: e.target.value })} className="select">
                  <option value="">Select a client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                  <select value={formData.project_type} onChange={(e) => setFormData({ ...formData, project_type: e.target.value as 'AI' | 'Marketing' })} className="select">
                    <option value="Marketing">Marketing</option>
                    <option value="AI">AI</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                  <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value as any })} className="select">
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="input" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Progress (%)</label>
                  <input type="number" min="0" max="100" value={formData.progress} onChange={(e) => setFormData({ ...formData, progress: parseInt(e.target.value) || 0 })} className="input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Budget</label>
                  <input type="number" step="0.01" value={formData.budget} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} className="input" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Deadline</label>
                <input type="date" value={formData.deadline} onChange={(e) => setFormData({ ...formData, deadline: e.target.value })} className="input" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary">{editingProject ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
